import * as decoding from 'lib0/decoding';
import * as encoding from 'lib0/encoding';
import * as syncProtocol from 'y-protocols/sync';
import { observeSyncReply } from '../network/sync-observation.ts';

import { isFeatureFlagEnabled } from '@canvas/client-feature-flags';
import { isValidReceivedMessage } from '@canvas/protocol/validate-received-message';

import { MessageType } from '@canvas/protocol';
import { decodeMessageV2, encodeMessageV2 } from '@canvas/protocol/v2';
import { captureException, CustomerImpact } from '@canvas/sentry-react';
import { YjsOrigin, YJS_DEFAULT } from '@canvas/transaction';

import { type CollabConnectionClientSender } from '../transport/collab-connection-transport.ts';

import { getAdaptiveBatchIntervalMs } from '../network/adaptive-batch-interval.ts';
import { type RelaySupplierResult } from '../network/socket.ts';
import {
  type Transport,
  type TransportCallbacks,
  type TransportContext,
  createCollabConnectionTransport,
} from '../transport/index.ts';

import { type CollabStrategy, type TransientMessageContext } from './collab-strategy.ts';

type NcsConnectionMode = 'local-direct' | 'tenant-proxy';

function getNcsConnectionMode(): NcsConnectionMode {
  const mode = process.env.VITE_NCS_CONNECTION_MODE;

  switch (mode) {
    case 'local-direct':
    case 'tenant-proxy':
      return mode;
    default:
      return process.env.NODE_ENV === 'development' ? 'local-direct' : 'tenant-proxy';
  }
}

export class NcsCollabStrategy implements CollabStrategy {
  readonly type = 'ncs' as const;

  createTransport(ctx: TransportContext, callbacks: TransportCallbacks): Transport {
    return createCollabConnectionTransport(ctx, callbacks);
  }

  sendAwareness(
    message: Uint8Array<ArrayBuffer>,
    transport: Transport | undefined,
    fallbackSend: (msg: Uint8Array<ArrayBuffer>) => void,
  ): void {
    if (transport?.sendAwareness) {
      transport.sendAwareness(message);
    } else {
      fallbackSend(message);
    }
  }

  getStepsUrl(params: {
    documentId: string;
    headStep: string;
    serverUrl?: string;
    attempt: number;
  }): string {
    const { documentId, headStep, serverUrl } = params;
    if (!serverUrl) {
      throw new Error('serverUrl is required for NCS steps URL');
    }
    return `${serverUrl}/api/_internal/document/${encodeURIComponent(
      documentId,
    )}/steps/${headStep}`;
  }

  getRelayUrl(params: {
    boardId: string;
    attempt: number;
    resourceType: 'whiteboard' | 'database' | 'slide';
    tenantId: string;
    activationId: string;
  }): RelaySupplierResult {
    const { boardId, resourceType, tenantId, activationId } = params;
    const ariResourceType = resourceType === 'whiteboard' ? 'board' : resourceType;
    const ncsBasePath =
      typeof globalThis.location !== 'undefined'
        ? `${globalThis.location.protocol}//${globalThis.location.host}`
        : process.env.VITE_NCS_SESSION_URL;

    if (!ncsBasePath) {
      throw new Error('ncsBasePath is not defined.');
    }

    if (getNcsConnectionMode() === 'local-direct') {
      const sessionAri = `ari:cloud:canvas:${tenantId}:${ariResourceType}/${boardId}`;
      return {
        url: `${ncsBasePath}/ncs-canvas/session/${sessionAri}`,
      };
    }

    // use PMR in micros
    return {
      url: `${ncsBasePath}/ncs-canvas/${tenantId}/${activationId}/confluence`,
      namespace: `ncs-canvas/session/ari:cloud:canvas:${tenantId}:${ariResourceType}/${boardId}`,
    };
  }

  isReceivedMessageValid(message: unknown): boolean {
    // NCS peers send protocol messages directly to one another, so the client must
    // validate inbound payloads itself. CF historically relied on the DO path.
    return isValidReceivedMessage(message);
  }

  onTransientMessage(
    event: Uint8Array<ArrayBuffer>,
    context: TransientMessageContext,
    sender?: CollabConnectionClientSender,
  ): void {
    const message = decodeMessageV2(event);
    if (
      !isValidReceivedMessage(message) ||
      (message.type !== MessageType.Awareness &&
        message.type !== MessageType.SyncPhase0 &&
        message.type !== MessageType.SyncPhase1 &&
        message.type !== MessageType.SyncPhase2)
    ) {
      captureException(new Error('Received invalid transient message'), {
        tags: {
          captureLocation: 'yjs-socket-invalid-transient-message',
          customerImpact: CustomerImpact.CUSTOMERS,
        },
      });
      return;
    }
    if (message.type === MessageType.Awareness) {
      context.onAwareness(message);
      return;
    }
    if (!context.checkCanSync()) {
      return;
    }
    switch (message.type) {
      case MessageType.SyncPhase0:
        this.onSyncPhase0(message, context, sender);
        break;
      case MessageType.SyncPhase1:
        this.onSyncPhase1(message, context, sender);
        break;
      case MessageType.SyncPhase2:
        this.onSyncPhase2(message, context);
        break;
    }
  }

  configureUpdateBatching({
    enableStateBridge,
    getCollaboratorCount,
  }: Parameters<CollabStrategy['configureUpdateBatching']>[0]): void {
    const getBatchIntervalMs = isFeatureFlagEnabled(
      'cc-content-types-adaptive-persistent-batching',
    )
      ? () => getAdaptiveBatchIntervalMs(getCollaboratorCount())
      : undefined;
    enableStateBridge(getBatchIntervalMs);
  }

  sendConnectSync({
    canSync,
    closeConnectTimeGap,
  }: Parameters<CollabStrategy['sendConnectSync']>[0]) {
    if (canSync) {
      return closeConnectTimeGap();
    }
  }

  onSyncPhase0(
    { data: { request, recipient } }: Parameters<CollabStrategy['onSyncPhase0']>[0],
    context: Parameters<CollabStrategy['onSyncPhase0']>[1],
    sender?: Parameters<CollabStrategy['onSyncPhase0']>[2],
  ): void {
    if (context.onSyncObservation) {
      return observeSyncReply(context, sender ?? recipient, (unobservedContext) => {
        this.onSyncPhase0(
          { type: MessageType.SyncPhase0, data: { request, recipient } },
          unobservedContext,
          sender,
        );
      });
    }
    const { doc, origin, send, sendToPeer } = context;
    const encoder = encoding.createEncoder();
    syncProtocol.readSyncMessage(
      decoding.createDecoder(request),
      encoder,
      doc,
      new YjsOrigin(YJS_DEFAULT, origin),
    );
    const syncReplyEncoder = encoding.createEncoder();
    syncProtocol.writeSyncStep1(syncReplyEncoder, doc);
    const message = encodeMessageV2({
      type: MessageType.SyncPhase1,
      data: {
        recipient: sender ?? recipient,
        request: encoding.toUint8Array(syncReplyEncoder),
        response: encoding.toUint8Array(encoder),
      },
    });
    if (sender) {
      sendToPeer(message, sender);
    } else {
      send(message);
    }
  }

  onSyncPhase1(
    { data: { request, response } }: Parameters<CollabStrategy['onSyncPhase1']>[0],
    {
      doc,
      origin,
      sendToPeer,
      completeLazySync,
    }: Parameters<CollabStrategy['onSyncPhase1']>[1],
    sender?: CollabConnectionClientSender,
  ): void {
    if (!sender) {
      captureException(new Error('Received sync phase 1 without peer sender'), {
        tags: {
          captureLocation: 'yjs-socket-sync-phase-1-missing-sender',
          customerImpact: CustomerImpact.CUSTOMERS,
        },
      });
      return;
    }
    const encoder = encoding.createEncoder();
    syncProtocol.readSyncMessage(
      decoding.createDecoder(request),
      encoder,
      doc,
      new YjsOrigin(YJS_DEFAULT, origin),
    );
    syncProtocol.readSyncMessage(
      decoding.createDecoder(response),
      encoding.createEncoder(),
      doc,
      new YjsOrigin(YJS_DEFAULT, origin),
    );
    completeLazySync();
    const message = encodeMessageV2({
      type: MessageType.SyncPhase2,
      data: { recipient: sender, response: encoding.toUint8Array(encoder) },
    });
    sendToPeer(message, sender);
  }

  onSyncPhase2(
    { data: { response } }: Parameters<CollabStrategy['onSyncPhase2']>[0],
    { doc, origin, resetLazySyncState }: Parameters<CollabStrategy['onSyncPhase2']>[1],
  ): void {
    syncProtocol.readSyncMessage(
      decoding.createDecoder(response),
      encoding.createEncoder(),
      doc,
      new YjsOrigin(YJS_DEFAULT, origin),
    );
    resetLazySyncState();
  }

  onUpdateApplied({
    hasPendingSyncState,
    lazySyncInFlight,
    resetLazySyncState,
    recordUnappliedUpdate,
    triggerLazySync,
  }: Parameters<CollabStrategy['onUpdateApplied']>[0]): void {
    if (lazySyncInFlight && !hasPendingSyncState) {
      resetLazySyncState();
    }
    if (hasPendingSyncState) {
      recordUnappliedUpdate();
    }
    triggerLazySync();
  }

  onLazySyncTimeout({
    hasPendingSyncState,
    resetLazySyncState,
    onTimeout,
  }: Parameters<CollabStrategy['onLazySyncTimeout']>[0]): void {
    if (!hasPendingSyncState) {
      resetLazySyncState();
      return;
    }
    onTimeout();
  }

  shouldHandleLazySync(): boolean {
    return true;
  }

  sendLazySyncRequest({
    message,
    lastUpdateSender,
    send,
    sendToPeer,
  }: Parameters<CollabStrategy['sendLazySyncRequest']>[0]): void {
    if (lastUpdateSender) {
      sendToPeer(message, lastUpdateSender);
      return;
    }
    send(message);
  }

  shouldDeferReconnectUpdate(): boolean {
    return true;
  }

  prepareReconnectUpdateChanges({
    stateBridge,
  }: Parameters<CollabStrategy['prepareReconnectUpdateChanges']>[0]): void {
    stateBridge.reset();
    stateBridge.offUpdate();
  }

  restoreReconnectUpdateChanges({
    stateBridge,
    onUpdate,
  }: Parameters<CollabStrategy['restoreReconnectUpdateChanges']>[0]): void {
    stateBridge.onUpdate(onUpdate);
  }

  prepareReconnectUpdate({
    stateBridge,
    update,
    clientID,
    clock,
  }: Parameters<CollabStrategy['prepareReconnectUpdate']>[0]): void {
    stateBridge.resetAndLoad({
      payload: update,
      origin: undefined,
      clientID,
      clock,
    });
  }

  resetModeState({ stateBridge }: Parameters<CollabStrategy['resetModeState']>[0]): void {
    stateBridge.reset();
  }
}
